import { useEffect, useState } from 'react'
import { Counter, Tag } from '../badgesAndTags/index.ts'
import Filter from '../inputs/filter.tsx'
import Loader from '../loaders/loader.tsx'
import {
	TypographyCaps,
	TypographyDisplay,
	TypographyHeading,
	TypographyParagraph,
} from '../typography/index.ts'
import { ArrowRightIcon, MoreIcon } from '../icons/index.ts'
import { isRouteErrorResponse, Link, useRouteError } from '@remix-run/react'
import { AttestationType, eventData } from '#types/loreTypes.tsx'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import EventsTable from './eventsTable.tsx'
import Button from '../buttons/button.tsx'

export default function Events({
	eventsData,
	count,
	origin,
}: {
	eventsData: AttestationType[]
	count: number
	origin: string
}) {
	return (
		<div className="mx-auto max-w-[1440px] px-4 py-16 md:px-12 lg:px-36">
			<EventsTableSection
				eventsData={eventsData}
				totalEvents={count}
				atomicLoreUrl={origin}
			/>
		</div>
	)
}

const EventsTableSection = ({
	eventsData,
	totalEvents,
	atomicLoreUrl,
}: {
	eventsData: AttestationType[]
	totalEvents: number
	atomicLoreUrl: string
}) => {
	const [eventsList, setEventsList] = useState<{
		page: number
		events: AttestationType[]
		totalEvents: number | null
	}>({
		page: 0,
		events: eventsData ? eventsData : [],
		totalEvents: totalEvents,
	})
	const [sortCriteria, setSortCriteria] = useState({
		title: 'Newest',
		description: 'DESC',
	})
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		eventsList.page == 0
			? loadEvents()
			: setEventsList({ ...eventsList, page: 0 })
	}, [sortCriteria])

	useEffect(() => {
		loadEvents()
	}, [eventsList.page])

	const loadEvents = async () => {
		setLoading(true)
		try {
			const options = {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
			}
			const response = await fetch(
				`/eventsAPI?page=${eventsList.page}&sortOrder=${sortCriteria.description}`,
				options,
			)
			const eventsResponse = (await response.json()) as {
				page: number
				eventsData: AttestationType[]
				count: number
			}
			if (eventsList.page == eventsResponse.page)
				setEventsList({
					page: eventsResponse.page,
					totalEvents: eventsResponse.count,
					events:
						eventsList.page == 0
							? eventsResponse.eventsData
							: [...eventsList.events, ...eventsResponse.eventsData],
				})
			setLoading(false)
		} catch (e) {
			console.error(e)
		}
	}

	const sort = [
		{ title: 'Newest', description: 'DESC' },
		{ title: 'Oldest', description: 'ASC' },
	]

	const canFetchNextPage = () => {
		return (
			eventsList.totalEvents == null ||
			eventsList.totalEvents > eventsList.events.length
		)
	}

	return (
		<div className="my-8 flex w-full flex-col sm:my-16">
			<div className="flex flex-col justify-between gap-y-6 lg:flex-row lg:items-end">
				<div className="flex flex-col gap-y-4 lg:max-w-sm">
					<TypographyCaps
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Atomic Lore
					</TypographyCaps>
					<TypographyDisplay
						size="large"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						Event Rollups
					</TypographyDisplay>
				</div>
				<div className="flex flex-col items-start lg:items-end">
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Track and explore all rollup events signed in Atomic Lore
					</TypographyParagraph>
					<Link
						to="https://calendly.com/atomicform-melissa/30min"
						target="_blank"
					>
						<Button variant="tertiary" leftIcon={<ArrowRightIcon />}>
							Schedule a demo
						</Button>
					</Link>
				</div>
			</div>
			<div className="mt-16 flex w-full flex-row justify-between">
				<div className="flex flex-row items-center gap-x-4">
					<TypographyHeading
						isBold={true}
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						All Rollups
					</TypographyHeading>
					<Counter size="small">{totalEvents}</Counter>
				</div>
				<Filter
					type="Sort"
					options={sort}
					value={sortCriteria}
					onChange={newVal => setSortCriteria(newVal)}
				/>
			</div>
			<div className="pt-8">
				{(eventsList.events && eventsList.events.length) || loading ? (
					<>
						{!eventsList.events ||
						!eventsList.events.length ||
						!loading ||
						eventsList.page != 0 ? (
							<EventsTable
								eventsAttestations={eventsList.events}
								atomicLoreUrl={atomicLoreUrl}
							/>
						) : (
							<div className="mx-auto mt-8 flex w-full flex-col items-center justify-center text-dark dark:text-light">
								<TypographyParagraph
									size="medium"
									additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
								>
									Loading events...
								</TypographyParagraph>
								<Loader size="medium" />
							</div>
						)}
						{canFetchNextPage() && (
							<div className="mt-6 flex w-full items-center justify-center">
								<Tag
									showIcon={false}
									disabled={loading}
									onClick={() =>
										!loading &&
										setEventsList({
											...eventsList,
											page: eventsList.page + 1,
										})
									}
								>
									<MoreIcon />
								</Tag>
							</div>
						)}
					</>
				) : (
					<div className="flex w-full flex-col items-center justify-center">
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							No events found.
						</TypographyParagraph>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							All events signed in Atomic Lore will be displayed in this page.
						</TypographyParagraph>
					</div>
				)}
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	const error = useRouteError()
	captureRemixErrorBoundaryError(error)

	if (isRouteErrorResponse(error)) {
		console.error('Error is:', error)
		return (
			<div title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading the events index page
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Status: {error.status} - {error.statusText}
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Data: {error.data}
				</TypographyParagraph>
			</div>
		)
	} else if (error instanceof Error) {
		console.error('Error is:', error)
		return (
			<div title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading the events index page
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Message: {error.message}
				</TypographyParagraph>
			</div>
		)
	} else {
		return (
			<div title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An unknown error occurred while loading the events index page
				</TypographyParagraph>
			</div>
		)
	}
}
